import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Plus, Search, Filter, MapPin, Eye } from 'lucide-react';
import { fmtDate, fmtTime, fmtDateTime } from '@/lib/dates';

export default function AgendaListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', kategori: 'all', search: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.kategori !== 'all') params.set('kategori', filters.kategori);
      if (filters.search) params.set('search', filters.search);
      const { data } = await api.get(`/agenda?${params.toString()}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.kategori, filters.search]);

  const canCreate = user?.role === 'admin' || user?.role === 'operator';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Daftar Agenda Pimpinan"
        subtitle="Kelola dan pantau seluruh agenda Wali Kota dan pejabat delegasi."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Agenda' }]}
        action={
          <>
            <Link to="/agenda/kalender"><Button variant="outline"><Calendar className="w-4 h-4 mr-2" />Tampilan Kalender</Button></Link>
            {canCreate && (
              <Link to="/agenda/baru"><Button data-testid="add-agenda-button" className="bg-[#DC2626] hover:bg-[#B91C1C]"><Plus className="w-4 h-4 mr-2" />Tambah Agenda</Button></Link>
            )}
          </>
        }
      />

      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="agenda-search-input"
                placeholder="Cari nama acara, lokasi, deskripsi…"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
                <SelectItem value="Sedang Berlangsung">Sedang Berlangsung</SelectItem>
                <SelectItem value="Selesai">Selesai</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.kategori} onValueChange={(v) => setFilters({ ...filters, kategori: v })}>
              <SelectTrigger className="w-full md:w-44" data-testid="filter-kategori"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kategori</SelectItem>
                <SelectItem value="Resmi">Resmi</SelectItem>
                <SelectItem value="Kunjungan">Kunjungan</SelectItem>
                <SelectItem value="Rapat">Rapat</SelectItem>
                <SelectItem value="Seremonial">Seremonial</SelectItem>
                <SelectItem value="Pribadi">Pribadi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-premium overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
        ) : items.length === 0 ? (
          <EmptyState title="Belum ada agenda" description="Tidak ada agenda sesuai filter. Ubah filter atau buat agenda baru." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="data-table">
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead className="w-32">Tanggal</TableHead>
                  <TableHead>Acara</TableHead>
                  <TableHead className="w-40 hidden md:table-cell">Kategori / Urgensi</TableHead>
                  <TableHead className="hidden lg:table-cell">Pejabat / Delegasi</TableHead>
                  <TableHead className="w-36">Status</TableHead>
                  <TableHead className="w-16 text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="font-semibold text-slate-800">{fmtDate(a.tanggal_mulai)}</div>
                      <div className="text-xs text-slate-500">{fmtTime(a.tanggal_mulai)} WITA</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-800">{a.nama_acara}</div>
                      <div className="text-xs text-slate-500"><MapPin className="w-3 h-3 inline mr-0.5" />{a.lokasi}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-slate-600">{a.kategori}</span>
                        <StatusBadge status={a.tingkat_urgensi} type="urgensi" />
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-xs">
                        <div className="font-medium text-slate-700">{a.pejabat_utama_nama || a.pejabat_utama}</div>
                        {a.delegasi_nama && <div className="text-slate-500">↳ Delegasi: {a.delegasi_nama}</div>}
                      </div>
                    </TableCell>
                    <TableCell><StatusBadge status={a.status} type="agenda" /></TableCell>
                    <TableCell className="text-right">
                      <Link to={`/agenda/${a.id}`}>
                        <Button size="icon" variant="ghost" data-testid={`view-agenda-${a.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
